/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment, type ReactNode, type RefObject, useMemo } from 'react';

import { jsx } from '@compiled/react';
import { type Table, type Row as TanstackRow } from '@tanstack/react-table';
import type { VirtualItem } from '@tanstack/react-virtual';

import { cssMap } from '@atlaskit/css';

import type { CharlieTableProps } from '../../charlie-table.types';
import type { ColumnVirtualizer } from '../../hooks/virtualization/use-charlie-column-virtualization';
import { useCharlieRowVirtualization } from '../../hooks/virtualization/use-charlie-row-virtualization';
import { TBody } from '../body';

import { DataRow } from './data-row';
import { GroupedRow } from './grouped-row';
import type { SharedRowProps } from './shared-row-props.types';

export type CharlieTableBodyProps<T extends object> = {
	tableBodyRef?: RefObject<HTMLTableSectionElement>;
	table: Table<T>;
	renderOptions?: CharlieTableProps<T>['renderOptions'];
	rowSpacing?: CharlieTableProps<T>['rowSpacing'];
	onRowClick?: CharlieTableProps<T>['onRowClick'];
	tableType?: CharlieTableProps<T>['tableType'];
	renderChildComponent?: CharlieTableProps<T>['renderChildComponent'];
	enableRowSelection?: CharlieTableProps<T>['enableRowSelection'];
	rowCellXcss?: CharlieTableProps<T>['rowCellXcss'];
	interactionName?: CharlieTableProps<T>['interactionName'];
	labels: CharlieTableProps<T>['labels'];
	analyticsContext?: CharlieTableProps<T>['analyticsContext'];
	enableKeyboardNavigation?: CharlieTableProps<T>['enableKeyboardNavigation'];
	tableNavigationControllers?: (event: React.KeyboardEvent<HTMLTableCellElement>) => void;
	containerRef: RefObject<HTMLDivElement>;
	enableRowVirtualization: boolean;
	rowVirtualizerRef?: NonNullable<CharlieTableProps<T>['refs']>['rowVirtualizerRef'];
	rowVirtualizerOptions?: CharlieTableProps<T>['rowVirtualizerOptions'];
	infiniteScrollRef?: RefObject<HTMLTableRowElement>;
	hasNext?: CharlieTableProps<T>['hasNext'];
	responsiveColumnSizing?: CharlieTableProps<T>['responsiveColumnSizing'];
	columnVirtualizer?: ColumnVirtualizer;
	enableRowReorder?: boolean;
	onDraggingRowEnd?: CharlieTableProps<T>['onDraggingRowEnd'];
	componentPropOverrides?: CharlieTableProps<T>['componentPropOverrides'];
};

const styles = cssMap({
	tableBody: {
		isolation: 'isolate',
	},
});

export const CharlieTableBody = <
	T extends object & { overrideRender?: (rowData: T) => ReactNode },
>({
	table,
	tableBodyRef,
	renderOptions,
	rowSpacing,
	onRowClick,
	tableType,
	renderChildComponent,
	enableRowSelection,
	rowCellXcss,
	interactionName,
	labels,
	enableKeyboardNavigation,
	tableNavigationControllers,
	analyticsContext,
	containerRef,
	enableRowVirtualization,
	rowVirtualizerRef,
	rowVirtualizerOptions,
	infiniteScrollRef,
	hasNext,
	responsiveColumnSizing,
	columnVirtualizer,
	enableRowReorder = false,
	onDraggingRowEnd: _onDraggingRowEnd,
	componentPropOverrides,
}: CharlieTableBodyProps<T>) => {
	const rowVirtualizer = useCharlieRowVirtualization({
		enableRowVirtualization,
		table,
		tableContainerRef: containerRef,
		rowSpacing,
		rowVirtualizerRef,
		rowVirtualizerOptions,
	});
	const { rows: rowsFromModel } = table.getRowModel();
	const rows = rowVirtualizer ? rowVirtualizer.getVirtualItems() : rowsFromModel;

	const { virtualColumns, virtualPaddingLeft, virtualPaddingRight } = columnVirtualizer ?? {};

	return (
		<TBody
			ref={tableBodyRef}
			xcss={styles.tableBody}
			style={{
				height: rowVirtualizer ? `${rowVirtualizer.getTotalSize()}px` : undefined,
			}}
		>
			{rows ? (
				rows.map((rowOrVirtualRow, index) => {
					const virtualRow = rowVirtualizer ? (rowOrVirtualRow as VirtualItem) : undefined;
					const row = rowVirtualizer
						? rowsFromModel[rowOrVirtualRow.index]
						: (rowOrVirtualRow as TanstackRow<T>);

					const visibleCells = row.getVisibleCells();
					const groupedRowBackground = row.getIsGrouped()
						? renderOptions?.groupedRowBackground?.({
								rowData: row.original,
								state: table.getState(),
							})
						: undefined;

					const isGroupedOrOverride =
						row.original?.overrideRender ||
						(row.getIsGrouped() && renderOptions?.groupingRow);

					const sharedRowProps: SharedRowProps<T> = {
						row,
						table,
						renderOptions,
						rowSpacing,
						onRowClick,
						virtualRow,
						rowIndex: rowOrVirtualRow.index,
						rowVirtualizer,
						componentPropOverrides,
						labels,
						interactionName,
						analyticsContext,
					};

					return (
						<Fragment key={row.id}>
							{isGroupedOrOverride ? (
								<GroupedRow<T>
									{...sharedRowProps}
									renderChildComponent={renderChildComponent}
									groupedRowBackground={groupedRowBackground}
								/>
							) : (
								<DataRow<T>
									{...sharedRowProps}
									rowIndex={virtualRow?.index ?? index}
									visibleCells={visibleCells}
									virtualColumns={virtualColumns}
									virtualPaddingLeft={virtualPaddingLeft}
									virtualPaddingRight={virtualPaddingRight}
									tableType={tableType}
									enableRowSelection={enableRowSelection}
									rowCellXcss={rowCellXcss}
									enableKeyboardNavigation={enableKeyboardNavigation}
									tableNavigationControllers={tableNavigationControllers}
									responsiveColumnSizing={responsiveColumnSizing}
									enableRowReorder={enableRowReorder}
								/>
							)}
							{row.getIsExpanded() && renderChildComponent && (
								<Fragment>
									{renderChildComponent({
										rowData: row,
										tableState: table.getState(),
									})}
								</Fragment>
							)}
							{hasNext &&
								!rowVirtualizer &&
								index === Math.floor((table.getRowModel().rows?.length ?? 0) * 0.92) && (
									<tr key="loadMoreTrigger" ref={infiniteScrollRef} />
								)}
						</Fragment>
					);
				})
			) : (
				<Fragment />
			)}
		</TBody>
	);
};

export const MemoCharlieTableBody = <
	T extends object & { overrideRender?: (rowData: T) => ReactNode },
>(
	props: CharlieTableBodyProps<T>,
) => {
	const {
		table: {
			options: {
				data,
				state: { rowSelection, sorting, expanded, columnOrder, grouping, columnVisibility },
			},
		},
		containerRef,
		rowSpacing,
		renderOptions,
		componentPropOverrides,
	} = props;
	return useMemo(
		() => <CharlieTableBody {...props} />,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			rowSpacing,
			rowSelection,
			sorting,
			expanded,
			columnOrder,
			grouping,
			columnVisibility,
			data,
			containerRef,
			renderOptions,
			componentPropOverrides,
		],
	);
};
